import { SpotifyIcon } from '../../../assets/icons/spotify';
import { createEmbedProvider } from '../factory';

/**
 * Spotify URL regex pattern
 * Matches: open.spotify.com/track/..., open.spotify.com/album/..., etc.
 * Captures the path after the domain in group 1.
 */
export const SPOTIFY_REGEX = /^https?:\/\/open\.spotify\.com\/(.+)$/;

/**
 * Get normalized pathname from Spotify URL (strips /embed prefix)
 */
function getNormalizedPath(url: string): string {
  try {
    const parsed = new URL(url);

    return parsed.pathname.replace(/^\/embed/, '');
  } catch {
    return '';
  }
}

/** Known Spotify content types */
const SPOTIFY_CONTENT_TYPES = ['track', 'album', 'playlist', 'episode', 'show', 'artist'] as const;

/**
 * Extract the content type from a Spotify URL path
 * Handles prefixes like /intl-de/ or /user/username/
 */
function getSpotifyContentType(url: string): string | null {
  const path = getNormalizedPath(url);

  return SPOTIFY_CONTENT_TYPES.find((type) => path.includes(type)) ?? null;
}

/**
 * Dynamic height based on Spotify content type
 * - episode/show: 232px (podcast player)
 * - track: 80px (single track compact player)
 * - album/playlist/artist: 380px (full player)
 */
function getSpotifyHeight(url: string): number {
  const type = getSpotifyContentType(url);

  if (type === 'episode' || type === 'show') {
    return 232;
  }
  if (type === 'track') {
    return 80;
  }

  return 380;
}

export const spotifyProvider = createEmbedProvider({
  id: 'spotify',
  label: 'Spotify',
  icon: <SpotifyIcon className="size-5" />,
  regex: SPOTIFY_REGEX,
  buildEmbedUrl: (_match, cleanedUrl) => {
    const normalizedPath = getNormalizedPath(cleanedUrl);

    return normalizedPath ? `https://open.spotify.com/embed${normalizedPath}` : cleanedUrl;
  },
  buildTitle: (url) => {
    const type = getSpotifyContentType(url);
    if (!type) return 'Spotify Embed';

    const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);

    return `Spotify ${typeLabel}`;
  },
  renderProps: {
    height: getSpotifyHeight,
    allow: 'encrypted-media',
  },
});
